<template>
    <v-app id="app">
        <v-app-bar :elevation="4" :height="55" class="px-2">
            <div class="d-flex justify-space-between align-center w-full">
                <div class="d-flex">
                    <inertia-link href="/customers">
                        <ApplicationLogo class="h-6" />
                    </inertia-link>
                </div>

                <div class="d-none d-lg-flex">
                    <div v-for="item in navigation" :key="item.label">
                        <breeze-nav-link
                            :href="item.href"
                            :active="item.active()"
                            v-if="item.show()"
                            class="mx-1"
                        >
                            {{ item.label }}
                        </breeze-nav-link>
                    </div>
                </div>

                <div class="d-none d-lg-flex">
                    <v-menu transition="slide-x-transition">
                        <template v-slot:activator="{ props }">
                            <v-btn
                                variant="outlined"
                                size="small"
                                v-bind="props"
                            >
                                {{ $page.props.auth.user.name }}
                            </v-btn>
                        </template>

                        <v-list density="compact" rounded>
                            <div
                                v-for="item in dropdownNavigation"
                                :key="item.label"
                            >
                                <v-list-item
                                    v-if="item.show()"
                                    :href="item.href"
                                >
                                    <v-list-item-title class="text-body-2">
                                        {{ item.label }}
                                    </v-list-item-title>
                                </v-list-item>
                            </div>
                        </v-list>
                    </v-menu>
                </div>

                <div class="d-flex d-lg-none">
                    <button
                        @click="mobileMenuOpen = !mobileMenuOpen"
                        class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out"
                    >
                        <svg
                            class="h-6 w-6"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <path
                                :class="{
                                    hidden: showingNavigationDropdown,
                                    'inline-flex': !showingNavigationDropdown,
                                }"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                            <path
                                :class="{
                                    hidden: !showingNavigationDropdown,
                                    'inline-flex': showingNavigationDropdown,
                                }"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </v-app-bar>

        <v-navigation-drawer
            v-model="mobileMenuOpen"
            temporary
            location="right"
        >
            <v-list density="compact">
                <div
                    v-for="item in [...navigation, ...dropdownNavigation]"
                    :key="item.label"
                >
                    <v-list-item
                        v-if="item.show()"
                        :href="item.href"
                        @click="mobileMenuOpen = false"
                    >
                        <v-list-item-title class="text-body-2">
                            {{ item.label }}
                        </v-list-item-title>
                    </v-list-item>
                </div>
            </v-list>
        </v-navigation-drawer>

        <v-main class="px-2 my-6">
            <breeze-validation-errors />

            <slot />
        </v-main>
    </v-app>
</template>

<script>
import ApplicationLogo from "@/Components/ApplicationLogo";
import BreezeDropdown from "@/Components/Dropdown";
import BreezeDropdownLink from "@/Components/DropdownLink";
import BreezeNavLink from "@/Components/NavLink";
import BreezeResponsiveNavLink from "@/Components/ResponsiveNavLink";
import Toast from "@/Components/Toast";
import BreezeValidationErrors from "@/Components/ValidationErrors";

export default {
    components: {
        ApplicationLogo,
        Toast,
        BreezeDropdown,
        BreezeDropdownLink,
        BreezeNavLink,
        BreezeResponsiveNavLink,
        BreezeValidationErrors,
    },
    data() {
        return {
            heatPumpsEnabled: process.env.MIX_HEAT_PUMPS_ENABLED === "true",
            showingNavigationDropdown: false,
            mobileMenuOpen: false,
            dropdownNavigation: [
                {
                    href: "/profile",
                    label: this.$t("nav.myProfile"),
                    show: () => true,
                },
                {
                    href: "/pricing-variables-panel",
                    label: this.$t("nav.Pricing variables panel"),
                    show: () => this.$page.props.auth.user.role === "admin",
                },
                {
                    href: "/margin-factors-panel",
                    label: this.$t("nav.Margin factors panel"),
                    show: () => this.$page.props.auth.user.role === "admin",
                },
                {
                    href: "/extra-feature-prices-panel",
                    label: this.$t("nav.Extra feature prices panel"),
                    show: () => this.$page.props.auth.user.role === "admin",
                },
                {
                    href: "/roof-sheathings-panel",
                    label: this.$t("nav.Roof sheathings panel"),
                    show: () =>
                        this.$page.props.auth.user.role === "admin" ||
                        this.$page.props.auth.user.role === "manager",
                },
                {
                    href: "/xtg-stock",
                    label: "XTG",
                    show: () =>
                        this.$page.props.auth.user.role === "admin" ||
                        this.$page.props.auth.user.role === "manager",
                },
                {
                    href: "/nova-calc2-horizon",
                    label: "Horizon",
                    show: () =>
                        this.$page.props.auth.user.role === "admin"
                },
                {
                    href: this.route("logout"),
                    label: this.$t("nav.logout"),
                    show: () => true,
                },
            ],
            navigation: [
                {
                    href: "/customers",
                    label: this.$t("nav.customers"),
                    active: () => this.$page.url.includes("customers"),
                    show: () => true,
                },
                {
                    href: "/heatpump",
                    label: this.$t("nav.pumps"),
                    active: () => this.$page.url.includes("heatpump"),
                    show: () =>
                        this.$page.props.auth.user.role !== "sales" &&
                        this.heatPumpsEnabled,
                },
                {
                    href: "/containers",
                    label: this.$t("nav.Containers"),
                    active: () => this.$page.url.includes("containers"),
                    show: () => this.$page.props.auth.user.role !== "sales" &&
                        this.heatPumpsEnabled,
                },
                {
                    href: "/solarpanel",
                    label: this.$t("nav.modules"),
                    active: () => this.$page.url.includes("solarpanel"),
                    show: () => this.$page.props.auth.user.role !== "sales",
                },
                {
                    href: "/inverter",
                    label: this.$t("nav.inverters"),
                    active: () => this.$page.url.includes("inverter"),
                    show: () => this.$page.props.auth.user.role !== "sales",
                },
                {
                    href: "/battery",
                    label: this.$t("nav.batteries"),
                    active: () => this.$page.url.includes("battery"),
                    show: () => this.$page.props.auth.user.role !== "sales",
                },
                {
                    href: "/users",
                    label: this.$t("nav.users"),
                    active: () => this.$page.url.includes("users"),
                    show: () => this.$page.props.auth.user.role === "admin",
                },
                {
                    href: "/installation-calculator",
                    label: this.$t("nav.calculator"),
                    active: () => this.$page.url.includes("installation-calculator"),
                    show: () => true,
                },
                {
                    href: "/partners-panel",
                    label: this.$t("nav.Partners panel"),
                    active: () => this.$page.url.includes("partners-panel"),
                    show: () => this.$page.props.auth.user.role !== "sales",
                },
                {
                    href: this.route("won-offers-index"),
                    label: this.$t("Won ofers"),
                    active: () => this.$page.url.includes("won-offers"),
                    show: () => this.$page.props.auth.user.role === "admin",
                },
                {
                    href: this.route("sales-performance-index"),
                    label: this.$t("Sales performance"),
                    active: () => this.$page.url.includes("sales-performance"),
                    show: () => this.$page.props.auth.user.role === "admin",
                },
                {
                    href: "/xtg-stock",
                    label: "XTG",
                    active: () => this.$page.url.includes("xtg-stock"),
                    show: () =>
                        this.$page.props.auth.user.role === "admin" ||
                        this.$page.props.auth.user.role === "manager",
                },
                {
                    href: this.route("admin-referrals.index"),
                    label: "Refer a friend",
                    active: () => this.$page.url.includes("admin-referrals"),
                    show: () =>
                        this.$page.props.auth.user.role === "admin"
                },
                {
                    href: this.route("admin-api.index"),
                    label: "API",
                    active: () => this.$page.url.includes("admin-api"),
                    show: () =>
                        this.$page.props.auth.user.role === "admin"
                }
            ],
        };
    },
};
</script>

<style>
#app {
    background-color: #f7f8f9;
}

.v-field__outline__notch {
    border: none !important;
}
</style>
